import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../app/app.environment';
import { ScannerUtil, ScanResult } from '../app/utils/scanner.util';
import {
  InvoiceHttpResponse,
  InvoicesHttpResponse,
} from '../modals/invoice.model';
import {
  BillCart,
  CityHttpResponse,
  CountryHttpResponse,
  CreditPaymentCart,
  CustomerLoyaltyResponse,
  CustomersHttpResponse,
  HoldHttpResponse,
  OrderHttpResponse,
  PaymentDueResponse,
  ProductHttpResponse,
  ProductSearchHttpResponse,
  QuotationsHttpResponse,
  RestaurantCategoryListResponse,
  RestaurantProductListResponse,
  SaleReturn,
  StateHttpResponse,
  TransactionHttpResponse,
  TransactionsHttpResponse,
  VoidItemResponse,
} from '../modals/product.modals';
import { IndustryService } from './industry.service';

@Injectable()
export class ProductDbService {
  apiUrl = environment.apiURL + 'v1';

  constructor(
    private httpClient: HttpClient,
    private industryService: IndustryService
  ) { }

  /**
   * Search products by text (product name search)
   * Used for both Retail and Mobile industries
   * Simple GET request with search parameter only
   */
  getAllProduct(
    searchText = '',
    groupId?: any,
    page?:number,
    pageSize?:number,
    // [RESTAURANT] categoryId?: number — Optional: pass to filter products by menu category.
    // When implementing category-tab filtering for Restaurant industry, uncomment and add:
    //   if (categoryId) { url += `&categoryid=${categoryId}`; }
    additionalParams?: any
  ): Observable<ProductHttpResponse> {
    // let url = `${this.apiUrl}/order/searchproducts?search=${searchText}`; //chages by nandhini
    let url = `${this.apiUrl}/order/searchproductslist?search=${searchText}`;
    if (groupId) {
      url += `&groupid=${groupId}`;
    }
    if(page){
      url += `&page=${page}`;
    }
    if(pageSize){
      url += `&pageSize=${pageSize}`;
    }

    return this.httpClient.get<ProductHttpResponse>(url);
  }

  getProductDetails(id: number, groupId?: number): Observable<ProductSearchHttpResponse> {
    let url = `${this.apiUrl}/order/productdetails/${id}`;
    if (groupId) {
      url += `?groupid=${groupId}`;
    }
    return this.httpClient.get<ProductSearchHttpResponse>(url);
  }


  // ── Restaurant Industry APIs ────────────────────────────────────────────────

  /**
   * Fetch the list of menu categories for the Restaurant industry.
   * GET /v1/order/catlist
   */
  getRestaurantCategories(): Observable<RestaurantCategoryListResponse> {
    return this.httpClient.get<RestaurantCategoryListResponse>(
      `${this.apiUrl}/order/catlist`,
    );
  }

  /**
   * Fetch products for the Restaurant industry, optionally filtered by category.
   * GET /v1/order/getallproducts?cat=<catId>
   *
   * @param catId — Category id to filter by. Pass empty string ('') to get all products.
   */
  getRestaurantProducts(catId: number | string = ''): Observable<RestaurantProductListResponse> {
    return this.httpClient.get<RestaurantProductListResponse>(
      `${this.apiUrl}/order/getallproducts?cat=${catId}`,
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────


  getAllTransaction(): Observable<TransactionsHttpResponse> {
    return this.httpClient.post<TransactionsHttpResponse>(
      `${this.apiUrl}/sale/transaction-list`,
      {},
    );
  }

  getHoldTransaction(): Observable<TransactionsHttpResponse> {
    return this.httpClient.get<TransactionsHttpResponse>(
      `${this.apiUrl}/order`,
    );
  }

  getAllQuotation(): Observable<QuotationsHttpResponse> {
    return this.httpClient.get<QuotationsHttpResponse>(
      `${this.apiUrl}/order/getallquotation`,
    );
  }

  getQuotationDetails(
    quotationUuid: string,
  ): Observable<TransactionHttpResponse> {
    return this.httpClient.get<TransactionHttpResponse>(
      `${this.apiUrl}/order/getquotationdetails/${quotationUuid}`,
    );
  }
  getAllInvoice(searchQuery?: string): Observable<InvoicesHttpResponse> {
    if (searchQuery && searchQuery.trim()) {
      return this.httpClient.get<InvoicesHttpResponse>(
        `${this.apiUrl}/sale?search=${encodeURIComponent(searchQuery.trim())}`,
      );
    } else {
      return this.httpClient.post<InvoicesHttpResponse>(
        `${this.apiUrl}/sale`,
        {},
      );
    }
  }

  // ===== SALES RETURN APIs =====

  /**
   * Get sales return list (for Sale History / Return tab, F3)
   * GET /v1/sale/salesreturnlist
   * GET /v1/sale/salesreturnlist?search=<query>  — when user types in search box
   */
  getSalesReturnList(searchQuery?: string, page?: number, pageSize?: number): Observable<InvoicesHttpResponse> {
    let url = `${this.apiUrl}/sale/salesreturnlist`;
    const params: string[] = [];

    if (searchQuery && searchQuery.trim()) {
      params.push(`search=${encodeURIComponent(searchQuery.trim())}`);
    }
    if (page != null) {
      params.push(`page=${page}`);
    }
    if (pageSize != null) {
      params.push(`pageSize=${pageSize}`);
    }
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.httpClient.get<InvoicesHttpResponse>(url);
  }


  /**
   * Get full sales return details by UUID (called when user selects a row from the return list)
   * GET /v1/sale/salesreturndetails/<uuid>
   * Response includes invoice header + salesitems[] to bind into cart
   */
  getSalesReturnDetails(uuid: string): Observable<InvoiceHttpResponse> {
    return this.httpClient.get<InvoiceHttpResponse>(
      `${this.apiUrl}/sale/salesreturndetails/${uuid}`,
    );
  }

  /**
   * Find item using PSID/Barcode/IMEI (direct scan)
   * 
   * Mobile Industry: Sends type detection in payload body
   *   - Barcode → { "type": "barcode" }
   *   - IMEI → { "type": "imei" }
   * 
   * Retail Industry: Empty payload {}
   */
  findItemUsingPSID(
    psid: string,
    groupId?: number,
    additionalParams?: any
  ): Observable<ProductSearchHttpResponse> {

    let url = `${this.apiUrl}/order/scanitem/${psid}`;

    if (groupId) {
      url += `?groupid=${groupId}`;
    }

    const payload: any = {};

    if (this.industryService.isMobile()) {
      const scanResult: ScanResult = ScannerUtil.detectInputType(psid);

      payload.type = scanResult.type;

      if (additionalParams?.mapping) {
        payload.mapping = additionalParams.mapping;
      }

      if (additionalParams?.variants) {
        payload.variants = additionalParams.variants;
      }

      return this.httpClient.get<ProductSearchHttpResponse>(url, {
        params: payload
      });
    }

    if (this.industryService.isRetail() || this.industryService.isRestaurant() || this.industryService.isPharmacy()) {
      return this.httpClient.post<ProductSearchHttpResponse>(url, payload);
    }

    throw new Error('Unsupported industry');
  }
  /**
   * Place order / Process payment
   * Mobile Industry: Adds mapping, variants, and free_items to payload
   * Retail Industry: Standard payload (no changes)
   */
  placeOrder(payload: BillCart, mobileExtras?: any): Observable<OrderHttpResponse> {
    let finalPayload: any = { ...payload };

    // Mobile Industry: Add mobile-specific fields & ensure mapping_id on cart items
    if (this.industryService.isMobile()) {
      if (finalPayload.cart && Array.isArray(finalPayload.cart)) {
        finalPayload.cart = finalPayload.cart.map((item: any) => ({
          ...item,
          mapping_id: item.mapping_id !== undefined ? item.mapping_id : (item.selectedMappingId ?? null),
        }));
      }
      if (mobileExtras) {
        if (mobileExtras.mapping) {
          finalPayload.mapping = mobileExtras.mapping;
        }
        if (mobileExtras.variants) {
          finalPayload.variants = mobileExtras.variants;
        }
        if (mobileExtras.free_items && Array.isArray(mobileExtras.free_items)) {
          finalPayload.free_items = mobileExtras.free_items;
        }
      }
    }

    return this.httpClient.post<OrderHttpResponse>(
      `${this.apiUrl}/order/placeorder`,
      finalPayload,
    );
  }

  /**
   * Exchange/Return order
   * Mobile Industry: Adds mapping, variants, and free_items to payload
   * Retail Industry: Standard payload (no changes)
   */
  exchangeOrder(payload: BillCart, mobileExtras?: any): Observable<OrderHttpResponse> {
    let finalPayload: any = { ...payload };

    // Mobile Industry: Add mobile-specific fields & ensure mapping_id on cart items
    if (this.industryService.isMobile()) {
      if (finalPayload.cart && Array.isArray(finalPayload.cart)) {
        finalPayload.cart = finalPayload.cart.map((item: any) => ({
          ...item,
          mapping_id: item.mapping_id !== undefined ? item.mapping_id : (item.selectedMappingId ?? null),
        }));
      }
      if (mobileExtras) {
        if (mobileExtras.mapping) {
          finalPayload.mapping = mobileExtras.mapping;
        }
        if (mobileExtras.variants) {
          finalPayload.variants = mobileExtras.variants;
        }
        if (mobileExtras.free_items && Array.isArray(mobileExtras.free_items)) {
          finalPayload.free_items = mobileExtras.free_items;
        }
      }
    }

    return this.httpClient.post<OrderHttpResponse>(
      `${this.apiUrl}/sale/returninvoice`,
      finalPayload,
    );
  }

  holdOrder(
    order_uuid: string,
    temp_Name: string,
  ): Observable<HoldHttpResponse> {
    const payload = { tempname: temp_Name };
    return this.httpClient.post<HoldHttpResponse>(
      `${this.apiUrl}/order/holdorder/${order_uuid}`,
      payload,
    );
  }

  tapSpecificHoldOrder(
    order_uuid: string,
  ): Observable<TransactionHttpResponse> {
    return this.httpClient.get<TransactionHttpResponse>(
      `${this.apiUrl}/order/getholdorder/${order_uuid}`,
    );
  }

  /**
   * Create quotation from order
   * Mobile Industry: Adds mapping and variants if provided
   * Retail Industry: Standard payload (no changes)
   */
  quotationOrder(
    order_uuid: string,
    validity: string,
    mobileExtras?: any
  ): Observable<TransactionHttpResponse> {
    let payload: any = { validity };

    // Mobile Industry: Add mobile-specific fields
    if (this.industryService.isMobile() && mobileExtras) {
      if (mobileExtras.mapping) {
        payload.mapping = mobileExtras.mapping;
      }
      if (mobileExtras.variants) {
        payload.variants = mobileExtras.variants;
      }
    }

    return this.httpClient.post<TransactionHttpResponse>(
      `${this.apiUrl}/order/quotationorder/${order_uuid}`,
      payload,
    );
  }

  // generateInvoice(uuid: string): Observable<OrderHttpResponse> {
  //     return this.httpClient
  //         .post<OrderHttpResponse>(`${this.apiUrl}/sale/generateinvoice/${uuid}`, { modelofpayment: 'CASH' })
  // }

  generateInvoice(orderId: string, transactionId: string, paymentId: string): Observable<InvoiceHttpResponse> {
    const payload = { transactionId, paymentId };
    return this.httpClient.post<InvoiceHttpResponse>(
      `${this.apiUrl}/sale/generateinvoice/${orderId}`,
      payload,
    );
  }

  printInvoice(orderId: string): Observable<InvoiceHttpResponse> {
    return this.httpClient.post<InvoiceHttpResponse>(
      `${this.apiUrl}/sale/printinvoice/${orderId}`,
      {},
    );
  }

  // Dummy API for getting credit amount
  getCreditAmount(
    mobileNumber: string,
    invoiceNumber: string,
  ): Observable<{ code: string; data: { creditAmount: number } }> {
    return this.httpClient.post<{
      code: string;
      data: { creditAmount: number };
    }>(`${this.apiUrl}/sale/creditamount`, {
      mobile: mobileNumber,
      creditnumber: invoiceNumber,
    });
  }

  voidItem(productDetails: any): Observable<VoidItemResponse> {
    return this.httpClient.post<VoidItemResponse>(
      `${this.apiUrl}/order/voiditem`,
      productDetails,
    );
  }

  // Suspense Account APIs
  generateSuspense(orderUuid: string): Observable<any> {
    return this.httpClient.post<any>(
      `${this.apiUrl}/sale/generatesuspense/${orderUuid}`,
      {},
    );
  }

  generateSuspenseInvoice(suspenseUuid: string): Observable<any> {
    return this.httpClient.post<any>(
      `${this.apiUrl}/sale/generatesuspenseinvoice/${suspenseUuid}`,
      {},
    );
  }

  /**
   * Update existing quotation
   * Mobile Industry: Adds mapping and variants if provided
   * Retail Industry: Standard payload (no changes)
   */
  updateQuotationOrder(quotationUuid: string, payload: any, mobileExtras?: any): Observable<any> {
    let finalPayload = { ...payload };

    // Mobile Industry: Add mobile-specific fields
    if (this.industryService.isMobile() && mobileExtras) {
      if (mobileExtras.mapping) {
        finalPayload.mapping = mobileExtras.mapping;
      }
      if (mobileExtras.variants) {
        finalPayload.variants = mobileExtras.variants;
      }
    }

    return this.httpClient.put<any>(
      `${this.apiUrl}/order/updatequotation/${quotationUuid}`,
      finalPayload,
    );
  }

  // Customer Management APIs
  getAllCustomers(): Observable<CustomersHttpResponse> {
    return this.httpClient.get<CustomersHttpResponse>(
      `${this.apiUrl}/order/getcustomer`,
    );
  }

  getPaymentDueList(customerId: number): Observable<PaymentDueResponse> {
    return this.httpClient.get<PaymentDueResponse>(
      `${this.apiUrl}/sale/paymentDuelist/${customerId}`,
    );
  }

  getSaleReturns(customerId: number): Observable<SaleReturn[]> {
    return this.httpClient.post<SaleReturn[]>(
      `${this.apiUrl}/sale/exchangelist`,
      { customer_id: customerId },
    );
  }

  processCustomerPayment(
    customerId: number,
    payload: CreditPaymentCart,
  ): Observable<OrderHttpResponse> {
    return this.httpClient.put<OrderHttpResponse>(
      `${this.apiUrl}/sale/customerSaleduelist/${customerId}`,
      payload,
    );
  }

  // Get offer prices for multiple barcodes in a pricing group
  getSellingGroupItems(
    groupId: number,
    barcodes: string[],
  ): Observable<ProductHttpResponse> {
    return this.httpClient.put<ProductHttpResponse>(
      `${this.apiUrl}/order/getsellinggrpitem/${groupId}`,
      { barcodes },
    );
  }

  // Customer Management APIs (temporary until backend is ready)
  createCustomer(customerData: any): Observable<any> {
    return this.httpClient.post<any>(
      `${this.apiUrl}/order/addcustomer`,
      customerData,
    );
  }

  updateCustomer(customerId: number, customerData: any): Observable<any> {
    return this.httpClient.put<any>(
      `${this.apiUrl}/order/updatecustomer/${customerId}`,
      customerData,
    );
  }

  getCustomerLoyaltyPoints(
    customerId: number,
  ): Observable<CustomerLoyaltyResponse> {
    return this.httpClient.get<CustomerLoyaltyResponse>(
      `${this.apiUrl}/order/customerredeempoint/${customerId}`,
    );
  }

  // Location APIs - Countries, States, Cities
  getCountries(): Observable<CountryHttpResponse> {
    return this.httpClient.get<CountryHttpResponse>(
      `${this.apiUrl}/order/countries`,
    );
  }

  getStates(countryId: number): Observable<StateHttpResponse> {
    return this.httpClient.get<StateHttpResponse>(
      `${this.apiUrl}/order/states/${countryId}`,
    );
  }

  getCities(stateId: number): Observable<CityHttpResponse> {
    return this.httpClient.get<CityHttpResponse>(
      `${this.apiUrl}/order/cities/${stateId}`,
    );
  }

  getLatestBillNo(): Observable<{
    status: number;
    message: string;
    data: { count: number };
  }> {
    return this.httpClient.get<{
      status: number;
      message: string;
      data: { count: number };
    }>(`${this.apiUrl}/sale/getlatestbillno`);
  }

  initiateUpiPayment(payload: {
    customerName: string;
    mobileNumber: string;
    amount: number;
    paymentMethod: string;
    paymentChannel: string;
  }): Observable<any> {
    return this.httpClient.post<any>(`${this.apiUrl}/order/upi-payment`, payload);
  }

  getUpiPaymentStatus(paymentId: string): Observable<any> {
    return this.httpClient.get<any>(`${this.apiUrl}/order/upi-payment/status/${paymentId}`);
  }
}
